(() => {
  if (window.__lastViewedProductsLoaded) return;
  window.__lastViewedProductsLoaded = true;
  if (window.rpdLastViewedEnabled === false) return;

  const STORAGE_KEY = 'last_viewed_products';
  const MAX_ITEMS = 20;
  const TTL_DAYS = 14;
  const TTL_MS = TTL_DAYS * 24 * 60 * 60 * 1000;
  const SECTION_SELECTOR = '[data-last-viewed-section]';
  const SECTION_READY_EVENT = 'rpd:deferred-section:ready';
  const HYDRATE_IMAGES_EVENT = 'rpd:deferred-card-images:hydrate';

  const safeJSONParse = (value) => {
    try {
      return JSON.parse(value);
    } catch (e) {
      return [];
    }
  };

  const readList = () => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      const list = safeJSONParse(raw);
      if (!Array.isArray(list)) return [];
      const now = Date.now();
      return list.filter((item) => item && item.viewedAt && now - item.viewedAt <= TTL_MS);
    } catch (e) {
      return [];
    }
  };

  const writeList = (list) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      // ignore
    }
  };

  const normalizeHandle = (handle) => {
    if (!handle) return '';
    return handle.toString().replace(/^\/products\//, '').replace(/^\//, '').split('?')[0];
  };

  const getStoredHandlesString = () =>
    readList()
      .filter((item) => item && item.handle)
      .map((item) => item.handle)
      .slice(0, MAX_ITEMS)
      .join(',');

  const buildFetchUrl = (section, handles) => {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', section.dataset.lastViewedFetchSectionId || 'param-last-viewed-items');
    url.searchParams.set('instance_id', section.dataset.sectionId || 'last-viewed-products');
    url.searchParams.set('lv_handles', handles);
    url.searchParams.set('products_limit', section.dataset.productsLimit || String(MAX_ITEMS));
    url.searchParams.set('columns_desktop', section.dataset.columnsDesktop || '4');
    url.searchParams.set('columns_mobile', section.dataset.columnsMobile || '2');
    url.searchParams.set('swipe_on_mobile', section.dataset.swipeOnMobile || 'false');
    url.searchParams.set('enable_desktop_slider', section.dataset.enableDesktopSlider || 'false');
    url.searchParams.set('hide_mobile_slider_buttons', section.dataset.hideMobileSliderButtons || 'false');
    url.searchParams.set('full_width', section.dataset.fullWidth || 'false');
    url.searchParams.set('image_ratio', section.dataset.imageRatio || 'adapt');
    url.searchParams.set('image_shape', section.dataset.imageShape || 'default');
    url.searchParams.set('show_secondary_image', section.dataset.showSecondaryImage || 'false');
    url.searchParams.set('show_vendor', section.dataset.showVendor || 'false');
    url.searchParams.set('show_rating', section.dataset.showRating || 'false');
    url.searchParams.set('swatch_type', section.dataset.swatchType || '');
    url.searchParams.set('swatch_ratio', section.dataset.swatchRatio || 'square');
    url.searchParams.set('swatch_per_row', section.dataset.swatchPerRow || '5');
    url.searchParams.set('swatch_max', section.dataset.swatchMax || '4');
    url.searchParams.set('quick_add', section.dataset.quickAdd || 'none');
    url.searchParams.set('title', section.dataset.title || '');
    url.searchParams.set('heading_size', section.dataset.headingSize || 'h2');
    url.searchParams.set('defer_image', section.dataset.deferImage || 'false');
    url.searchParams.set('compact_mobile', section.dataset.compactMobile || 'false');
    if (section.dataset.currentHandle) {
      url.searchParams.set('current_handle', section.dataset.currentHandle);
    }
    return url.toString();
  };

  const escapeHtml = (value) =>
    String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');

  const formatMoney = (amountInCents) => {
    const amount = Number(amountInCents || 0) / 100;
    const currency = window.Shopify?.currency?.active || 'EUR';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'nl-NL', {
        style: 'currency',
        currency,
      }).format(amount);
    } catch (error) {
      return `€${amount.toFixed(2)}`;
    }
  };

  const imageUrlWithWidth = (src, width) => {
    if (!src) return '';
    const separator = src.includes('?') ? '&' : '?';
    return `${src}${separator}width=${width}`;
  };

  const getVariantForItem = (product, variantId) => {
    if (!product) return null;
    if (variantId) {
      const matched = product.variants?.find((variant) => String(variant.id) === String(variantId));
      if (matched) return matched;
    }
    return product.variants?.find((variant) => variant.available) || product.variants?.[0] || null;
  };

  const getImageData = (product, variant) => {
    const variantImage = variant?.featured_image || variant?.featured_media?.preview_image || null;
    const mediaImage = Array.isArray(product?.media) ? product.media.find((item) => item?.media_type === 'image') : null;
    const mediaPreview = mediaImage?.preview_image || null;
    const src =
      variantImage?.src ||
      mediaImage?.src ||
      mediaPreview?.src ||
      product?.featured_image ||
      product?.images?.[0] ||
      '';
    const width = variantImage?.width || mediaImage?.width || mediaPreview?.width || 800;
    const height = variantImage?.height || mediaImage?.height || mediaPreview?.height || 800;
    const alt = variantImage?.alt || mediaImage?.alt || product?.title || '';
    return { src, width, height, alt };
  };

  const buildProductUrl = (product, variant) => {
    const baseUrl = product?.url || `/products/${product?.handle || ''}`;
    if (!variant?.id) return baseUrl;
    return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}variant=${variant.id}`;
  };

  const renderFallbackCard = (product, item, section, index, options) => {
    const variant = getVariantForItem(product, item.variantId);
    const productUrl = buildProductUrl(product, variant);
    const image = getImageData(product, variant);
    const imageSrc = imageUrlWithWidth(image.src, 360);
    const imageSrcset = [165, 300, 360]
      .map((width) => `${imageUrlWithWidth(image.src, width)} ${width}w`)
      .join(', ');
    const imageRatio = section.dataset.imageRatio || 'square';
    const ratioPercent = imageRatio === 'portrait' ? '125%' : imageRatio === 'adapt' && image.width && image.height
      ? `${(image.height / image.width) * 100}%`
      : '100%';
    const slideClass = options.showMobileSlider || options.showDesktopSlider ? ' slider__slide' : '';

    return `
      <li id="Slide-${section.dataset.sectionId}-${index + 1}" class="grid__item${slideClass}">
        <div
          class="card-wrapper product-card-wrapper underline-links-hover"
          data-product-available="${product.available ? 'true' : 'false'}"
          data-product-url="${escapeHtml(product.url || '')}"
          data-product-handle="${escapeHtml(product.handle || '')}"
        >
          <div class="card card--standard card--media" style="--ratio-percent: ${ratioPercent};">
            <div class="card__inner color-scheme-1 gradient ratio" style="--ratio-percent: ${ratioPercent};">
              <div class="card__media">
                <a href="${escapeHtml(productUrl)}" class="card__media-link full-unstyled-link" aria-label="${escapeHtml(product.title || '')}">
                  <div class="media media--transparent media--hover-effect">
                    <img
                      src="${escapeHtml(imageSrc)}"
                      srcset="${escapeHtml(imageSrcset)}"
                      sizes="(min-width: 1100px) calc((1100px - 130px) / ${section.dataset.columnsDesktop || 4}), (min-width: 750px) calc((100vw - 160px) / ${section.dataset.columnsMobile || 2}), calc((100vw - 80px) / ${section.dataset.columnsMobile || 2})"
                      alt="${escapeHtml(image.alt)}"
                      class="motion-reduce"
                      loading="lazy"
                      fetchpriority="low"
                      decoding="async"
                      width="${image.width}"
                      height="${image.height}"
                    >
                  </div>
                </a>
              </div>
              <div class="card__content">
                <div class="card__information">
                  <h3 class="card__heading">
                    <a href="${escapeHtml(productUrl)}" class="full-unstyled-link">${escapeHtml(product.title || '')}</a>
                  </h3>
                </div>
              </div>
            </div>
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading h5">
                  <a href="${escapeHtml(productUrl)}" class="full-unstyled-link">${escapeHtml(product.title || '')}</a>
                </h3>
                <div class="card-information">
                  <div class="price">
                    <div class="price__container">
                      <div class="price__regular">
                        <span class="price-item price-item--regular">${escapeHtml(formatMoney(variant?.price || product.price))}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </li>
    `;
  };

  const renderFallbackMarkup = (section, items, products) => {
    const columnsDesktop = Number(section.dataset.columnsDesktop || 4) || 4;
    const columnsMobile = Number(section.dataset.columnsMobile || 2) || 2;
    const showMobileSlider = section.dataset.swipeOnMobile === 'true' && products.length > columnsMobile;
    const showDesktopSlider = section.dataset.enableDesktopSlider === 'true' && products.length > columnsDesktop;
    const sliderClasses = `${showDesktopSlider ? ' slider--desktop' : ''}${showMobileSlider ? ' slider--tablet grid--peek' : ''}`;
    const pageWidthClass = !showMobileSlider ? ' page-width' : '';
    const desktopWidthClass = !showDesktopSlider && section.dataset.fullWidth !== 'true' ? ' page-width-desktop' : '';
    const fullWidthClass = section.dataset.fullWidth === 'true' ? ' collection--full-width' : '';
    const sliderComponentClasses = `${section.dataset.fullWidth === 'true' ? ' slider-component-full-width' : ''}${pageWidthClass}${desktopWidthClass}${showDesktopSlider ? ' slider-component-desktop' : ''}`;
    const titleWrapperClasses = `${showMobileSlider ? ' title-wrapper--self-padded-tablet-down' : ''}${showDesktopSlider ? ' collection__title--desktop-slider' : ''}`;
    const sliderButtons = showMobileSlider || showDesktopSlider
      ? `
        <div class="slider-buttons${section.dataset.hideMobileSliderButtons === 'true' ? ' slider-buttons--hide-mobile' : ''}">
          <button type="button" class="slider-button slider-button--prev" name="previous" aria-label="Previous slide" aria-controls="Slider-${section.dataset.sectionId}">
            <span class="svg-wrapper" aria-hidden="true">&#8249;</span>
          </button>
          <div class="slider-counter caption">
            <span class="slider-counter--current">1</span>
            <span aria-hidden="true"> / </span>
            <span class="slider-counter--total">${products.length}</span>
          </div>
          <button type="button" class="slider-button slider-button--next" name="next" aria-label="Next slide" aria-controls="Slider-${section.dataset.sectionId}">
            <span class="svg-wrapper" aria-hidden="true">&#8250;</span>
          </button>
        </div>
      `
      : '';

    return `
      <div class="collection section-${section.dataset.sectionId}-padding${fullWidthClass}">
        <div class="collection__title title-wrapper title-wrapper--no-top-margin page-width${titleWrapperClasses}">
          ${section.dataset.title ? `<h2 class="title inline-richtext ${escapeHtml(section.dataset.headingSize || 'h2')}">${escapeHtml(section.dataset.title)}</h2>` : ''}
        </div>
        <slider-component class="slider-mobile-gutter${sliderComponentClasses}">
          <ul
            id="Slider-${section.dataset.sectionId}"
            data-id="${escapeHtml(section.dataset.sectionId || 'last-viewed-products')}"
            class="grid product-grid contains-card contains-card--product grid--${columnsDesktop}-col-desktop grid--${columnsMobile}-col-tablet-down${showMobileSlider || showDesktopSlider ? ` slider${sliderClasses}` : ''}"
            role="list"
            aria-label="${escapeHtml(section.dataset.title || 'Last viewed')}"
          >
            ${products.map((product, index) => renderFallbackCard(product, items[index], section, index, { showMobileSlider, showDesktopSlider })).join('')}
          </ul>
          ${sliderButtons}
        </slider-component>
      </div>
    `;
  };

  const fetchProductJson = async (handle) => {
    const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin' });
    if (!response.ok) return null;
    return response.json();
  };

  const parseFetchResponse = async (response) => {
    if (!response.ok) return null;
    const text = await response.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');
    return doc.querySelector('[data-param-last-viewed-items]');
  };

  const fetchSectionMarkup = async (section, handles) => {
    const url = buildFetchUrl(section, handles);
    const response = await fetch(url, { credentials: 'same-origin' });
    const node = await parseFetchResponse(response);
    if (!node) return false;
    if (!node.querySelector('.card-wrapper[data-product-handle]')) return false;
    section.innerHTML = node.innerHTML;
    section.classList.remove('hidden');
    document.dispatchEvent(
      new CustomEvent(HYDRATE_IMAGES_EVENT, {
        detail: { scope: section },
      })
    );
    return true;
  };

  const renderFallbackFromJson = async (section, items) => {
    const products = await Promise.all(items.map((item) => fetchProductJson(item.handle)));
    const availableProducts = products
      .map((product, index) => ({ product, item: items[index] }))
      .filter(({ product }) => product && product.available !== false);
    if (!availableProducts.length) return false;
    section.innerHTML = renderFallbackMarkup(
      section,
      availableProducts.map(({ item }) => item),
      availableProducts.map(({ product }) => product)
    );
    section.classList.remove('hidden');
    return true;
  };

  const getCartCookieToken = () => {
    const match = document.cookie.match(/(?:^|;\s*)cart=([^;]+)/);
    if (!match || !match[1]) return 'no-cart';
    try {
      return decodeURIComponent(match[1]);
    } catch (error) {
      return match[1];
    }
  };

  const upsertItem = (handle, variantId) => {
    const normalizedHandle = normalizeHandle(handle);
    if (!normalizedHandle) return;

    const list = readList();
    const next = list.filter((item) => item && item.handle && item.handle !== normalizedHandle);
    next.unshift({
      handle: normalizedHandle,
      variantId: variantId ? String(variantId) : null,
      viewedAt: Date.now(),
    });

    writeList(next.slice(0, MAX_ITEMS));
  };

  const getProductData = () => {
    const element = document.querySelector('[data-last-viewed-product]');
    if (!element) return null;

    const handle = element.getAttribute('data-product-handle');
    if (!handle) return null;

    const params = new URLSearchParams(window.location.search);
    const urlVariantId = params.get('variant');
    const variantId = urlVariantId || element.getAttribute('data-variant-id') || null;

    return { handle, variantId };
  };

  const getProductDataFallback = () => {
    const pageProduct = window.rpdPageContext && window.rpdPageContext.product;
    if (!pageProduct || !pageProduct.handle) return null;
    const variantId = pageProduct.variant && pageProduct.variant.id ? String(pageProduct.variant.id) : null;
    return { handle: pageProduct.handle, variantId };
  };

  const updateCardLinks = (card, variantId) => {
    if (!card || !variantId) return;

    const baseUrl = card.dataset.productUrl;
    if (!baseUrl) return;

    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('variant', variantId);
    const nextUrl = `${url.pathname}${url.search}${url.hash}`;

    card.querySelectorAll('a[href]').forEach((link) => {
      const linkUrl = new URL(link.getAttribute('href'), window.location.origin);
      if (linkUrl.pathname === url.pathname) {
        link.setAttribute('href', nextUrl);
      }
    });

    card.querySelectorAll('[data-product-url]').forEach((el) => {
      const dataUrl = el.getAttribute('data-product-url');
      if (!dataUrl) return;
      const linkUrl = new URL(dataUrl, window.location.origin);
      if (linkUrl.pathname === url.pathname) {
        el.setAttribute('data-product-url', nextUrl);
      }
    });

    card.querySelectorAll('input.product-variant-id').forEach((input) => {
      input.value = variantId;
      input.removeAttribute('disabled');
    });
  };

  const applyVariantToCard = (card, variantId) => {
    if (!card || !variantId) return;
    const swatch = card.querySelector(`.card__swatch[data-variant-id="${variantId}"]`);
    if (swatch) {
      swatch.click();
      return;
    }
    updateCardLinks(card, variantId);
  };

  const dispatchLastViewedSyncedEvent = (handles) => {
    document.dispatchEvent(
      new CustomEvent('last-viewed:cart-attributes-synced', {
        detail: { handles },
      })
    );
  };

  const syncCartAttributes = (handles) => {
    if (!handles) return;
    try {
      const cacheKey = 'last_viewed_handles_synced';
      const cartToken = getCartCookieToken();
      const cacheToken = `${cartToken}|${handles}`;
      const lastSynced = window.sessionStorage.getItem(cacheKey);
      if (lastSynced === cacheToken) return;
      window.sessionStorage.setItem(cacheKey, cacheToken);
    } catch (e) {
      // ignore
    }

    fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { last_viewed_handles: handles } }),
      keepalive: true,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Unable to sync last viewed handles (${response.status})`);
        dispatchLastViewedSyncedEvent(handles);
      })
      .catch(() => {});
  };

  const hydrateSection = async (section) => {
    if (!section || section.dataset.loaded === 'true') return;

    const limit = Number(section.dataset.productsLimit || MAX_ITEMS) || MAX_ITEMS;
    const currentHandle = section.dataset.currentHandle || '';

    let items = readList().filter((item) => item && item.handle);
    if (currentHandle) {
      items = items.filter((item) => item.handle !== currentHandle);
    }

    items = items.slice(0, limit);

    if (!items.length) {
      section.classList.add('hidden');
      section.dataset.loaded = 'true';
      return;
    }

    let cards = section.querySelectorAll('.card-wrapper[data-product-handle]');
    if (!cards.length) {
      const fetched = await fetchSectionMarkup(
        section,
        items
          .map((item) => item.handle)
          .filter(Boolean)
          .join(',')
      );
      if (!fetched) {
        const fallbackRendered = await renderFallbackFromJson(section, items);
        if (!fallbackRendered) {
          section.classList.add('hidden');
          section.dataset.loaded = 'true';
          return;
        }
      }
      cards = section.querySelectorAll('.card-wrapper[data-product-handle]');
      if (!cards.length) {
        section.classList.add('hidden');
        section.dataset.loaded = 'true';
        return;
      }
    }

    items.forEach((item) => {
      if (!item.variantId) return;
      const card = section.querySelector(`.card-wrapper[data-product-handle="${item.handle}"]`);
      applyVariantToCard(card, item.variantId);
    });

    section.querySelectorAll('slider-component').forEach((slider) => {
      if (typeof slider.resetPages === 'function') {
        slider.resetPages();
      }
    });

    section.dataset.loaded = 'true';
  };

  const scheduleHydration = (section) => {
    if (!section || section.dataset.lastViewedHydrationScheduled === 'true') return;
    section.dataset.lastViewedHydrationScheduled = 'true';

    const load = () => {
      hydrateSection(section);
    };

    if (
      section.dataset.deferredSectionReady === 'true' ||
      !section.hasAttribute('data-deferred-section') ||
      (window.Shopify && window.Shopify.designMode)
    ) {
      load();
      return;
    }

    section.addEventListener(SECTION_READY_EVENT, load, { once: true });
  };

  const init = () => {
    const initialHandles = getStoredHandlesString();
    syncCartAttributes(initialHandles);

    const productData = getProductData() || getProductDataFallback();
    if (productData) {
      upsertItem(productData.handle, productData.variantId);
      syncCartAttributes(getStoredHandlesString());

      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
        subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (!data || !data.variant) return;
          upsertItem(productData.handle, data.variant.id);
          syncCartAttributes(getStoredHandlesString());
        });
      }
    }

    document.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      scheduleHydration(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      if (section.dataset.deferredSectionReady === 'true' || !section.hasAttribute('data-deferred-section')) {
        hydrateSection(section);
      }
    });
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (!event.target) return;
    const sections = event.target.matches?.(SECTION_SELECTOR)
      ? [event.target]
      : event.target.querySelectorAll?.(SECTION_SELECTOR);
    if (!sections) return;
    sections.forEach((section) => {
      scheduleHydration(section);
    });
  });
})();
